import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import { postData } from "../../../../Services/Fetchnodeservices";

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Dynamically import leaflet-control-geocoder
let Geocoder;
if (typeof window !== 'undefined') {
  Geocoder = require('leaflet-control-geocoder').Geocoder;
}

// Icons
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const SaveIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const SpinnerIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" 
      style={{ animation: "spin 0.8s linear infinite", transformOrigin: "center" }} />
  </svg>
);

const LocationIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

// Component to handle map clicks
function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Component to handle geocoder search
function GeocoderControl({ onLocationFound }) {
  const map = useMap();
  
  useEffect(() => {
    if (!map || !Geocoder) return;
    
    const geocoder = Geocoder.nominatim({
      placeholder: 'Search location...',
      defaultMarkGeocode: false
    });
    
    const control = geocoder.addTo(map);
    
    control.on('markgeocode', (e) => {
      const { center, name } = e.geocode;
      map.setView(center, 15);
      onLocationFound(center.lat, center.lng, name);
    });
    
    return () => {
      map.removeControl(control);
    };
  }, [map, onLocationFound]);
  
  return null;
}

// Component to handle current location
function CurrentLocationControl({ onLocationFound }) {
  const map = useMap();
  
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation not supported");
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        map.setView([latitude, longitude], 15);
        onLocationFound(latitude, longitude);
      },
      (error) => {
        alert("Could not get location: " + error.message);
      }
    );
  };
  
  // Add custom control button
  useEffect(() => {
    const CustomControl = L.Control.extend({
      options: { position: 'topright' },
      onAdd: () => {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom');
        container.innerHTML = '📍';
        container.style.backgroundColor = 'white';
        container.style.width = '30px';
        container.style.height = '30px';
        container.style.display = 'flex';
        container.style.alignItems = 'center';
        container.style.justifyContent = 'center';
        container.style.fontSize = '18px';
        container.style.cursor = 'pointer';
        container.style.borderRadius = '4px';
        container.style.border = '1px solid #ccc';
        container.onclick = getCurrentLocation;
        return container;
      }
    });
    
    const control = new CustomControl();
    map.addControl(control);
    
    return () => {
      map.removeControl(control);
    };
  }, [map]);
  
  return null;
}

// Reverse geocoding function using Nominatim
const reverseGeocode = async (lat, lng) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
    );
    const data = await response.json();
    return data.display_name || `${lat}, ${lng}`;
  } catch (error) {
    console.error("Reverse geocoding error:", error);
    return `${lat}, ${lng}`;
  }
};

// Forward geocoding function
const forwardGeocode = async (query) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`
    );
    const data = await response.json();
    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon),
        address: data[0].display_name
      };
    }
    return null;
  } catch (error) {
    console.error("Forward geocoding error:", error);
    return null;
  }
};

const LocationTabComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const mapRef = useRef(null);
  
  // Receive data from navigation state
  const { restroId, restaurantName, address, latitude, longitude, isDark } = location.state || {};
  console.log(restroId, restaurantName, address, latitude, longitude, isDark,"data");
  
  
  const [formData, setFormData] = useState({
    address: address || "",
    latitude: latitude || "",
    longitude: longitude || "",
  });

  const [markerPosition, setMarkerPosition] = useState({
    lat: parseFloat(latitude) || 28.6139,
    lng: parseFloat(longitude) || 77.2090,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [savingLocation, setSavingLocation] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Handle map click
  const handleMapClick = async (lat, lng) => {
    setMarkerPosition({ lat, lng });
    setSaveMessage({ type: "info", text: "Fetching address..." });
    
    const fetchedAddress = await reverseGeocode(lat, lng);
    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng,
      address: fetchedAddress,
    }));
    
    setSaveMessage({ type: "success", text: `📍 Location selected: ${lat.toFixed(4)}, ${lng.toFixed(4)}` });
    setTimeout(() => setSaveMessage(null), 2000);
  };

  // Handle location found from search or geolocation
  const handleLocationFound = async (lat, lng, addressParam = null) => {
    setMarkerPosition({ lat, lng });
    
    let finalAddress = addressParam;
    if (!finalAddress) {
      finalAddress = await reverseGeocode(lat, lng);
    }
    
    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng,
      address: finalAddress,
    }));
    
    if (mapRef.current) {
      mapRef.current.setView([lat, lng], 15);
    }
  };

  // Search for location by query
  const searchLocation = async () => {
    if (!searchQuery.trim()) {
      setSaveMessage({ type: "error", text: "Please enter a location to search!" });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    setIsSearching(true);
    setSaveMessage({ type: "info", text: "Searching..." });

    const result = await forwardGeocode(searchQuery);
    
    if (result) {
      setMarkerPosition({ lat: result.lat, lng: result.lng });
      setFormData({
        address: result.address,
        latitude: result.lat,
        longitude: result.lng,
      });
      
      if (mapRef.current) {
        mapRef.current.setView([result.lat, result.lng], 15);
      }
      
      setSaveMessage({ type: "success", text: `📍 Found: ${result.address.substring(0, 100)}...` });
      setSearchQuery("");
    } else {
      setSaveMessage({ type: "error", text: `❌ Location not found: "${searchQuery}"` });
    }
    
    setIsSearching(false);
    setTimeout(() => setSaveMessage(null), 3000);
  };

  // Get user's current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setSaveMessage({ type: "error", text: "❌ Geolocation not supported in your browser!" });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    setSaveMessage({ type: "info", text: "📍 Getting your location..." });

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        setMarkerPosition({ lat, lng });
        
        const fetchedAddress = await reverseGeocode(lat, lng);
        setFormData(prev => ({
          ...prev,
          latitude: lat,
          longitude: lng,
          address: fetchedAddress,
        }));
        
        if (mapRef.current) {
          mapRef.current.setView([lat, lng], 15);
        }
        
        setSaveMessage({ type: "success", text: `✅ Location found! ${fetchedAddress.substring(0, 100)}...` });
        setTimeout(() => setSaveMessage(null), 4000);
      },
      (error) => {
        let errorMessage = "❌ Could not get your location.";
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "❌ Location permission denied. Please enable location access.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "❌ Location information unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage = "❌ Location request timed out.";
            break;
          default:
            errorMessage = "❌ Could not get your location. Check permissions!";
        }
        setSaveMessage({ type: "error", text: errorMessage });
        setTimeout(() => setSaveMessage(null), 3000);
      }
    );
  };

  // Save location to API (only update API)
  const saveLocationToAPI = async () => {
    if (!formData.latitude || !formData.longitude) {
      setSaveMessage({ type: "error", text: "❌ Please select a location on the map first!" });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    setSavingLocation(true);
    setSaveMessage(null);

    try {
      const payload = {
        restroId: restroId,
        address: formData.address,
        latitude: parseFloat(formData.latitude).toString(),
        longitude: parseFloat(formData.longitude).toString(),
      };

      const response = await postData("restaurant/update_location", payload);

      if (response && response.status === true) {
        setSaveMessage({ type: "success", text: "✅ Location saved successfully!" });
        
        // Store updated location in sessionStorage to update parent component
        sessionStorage.setItem('updatedRestaurantLocation', JSON.stringify({
          address: formData.address,
          latitude: formData.latitude,
          longitude: formData.longitude
        }));
        
        // Go back to previous page after 1 second
        setTimeout(() => {
          navigate(-1);
        }, 1000);
      } else {
        setSaveMessage({ type: "error", text: response?.message || "❌ Failed to save location" });
      }
    } catch (error) {
      console.error("Error saving location:", error);
      setSaveMessage({ type: "error", text: "❌ Network error. Please try again." });
    } finally {
      setSavingLocation(false);
      setTimeout(() => setSaveMessage(null), 4000);
    }
  };

  // Go back to previous page
  const handleGoBack = () => {
    navigate(-1);
  };

  // Custom marker icon
  const customIcon = new L.Icon({
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
    iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });

  // Get theme styles
  const getThemeStyles = () => ({
    background: isDark ? "#0d1117" : "#f8fafc",
    surface: isDark ? "#141824" : "#ffffff",
    border: isDark ? "#1e2740" : "#e2e8f0",
    text: isDark ? "#f1f5f9" : "#1e293b",
    textSecondary: isDark ? "#94a3b8" : "#64748b",
  });

  const theme = getThemeStyles();

  return (
    <div style={{ minHeight: "100vh", background: theme.background, padding: "20px" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .leaflet-control-geocoder-form input {
          font-size: 12px !important;
          padding: 6px 10px !important;
        }
        .leaflet-control-geocoder-icon {
          background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18"><circle cx="11" cy="11" r="8" fill="none" stroke="currentColor" stroke-width="2"/><line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" stroke-width="2"/></svg>') !important;
          background-size: 16px !important;
          background-position: center !important;
          background-repeat: no-repeat !important;
        }
      `}</style>

      {/* Header with Back Button */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "16px",
        marginBottom: "20px",
        paddingBottom: "12px",
        borderBottom: `1px solid ${theme.border}`,
      }}>
        <button
          onClick={handleGoBack}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            background: "none",
            border: "none",
            color: "#3b82f6",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          <BackIcon /> Back
        </button>
        <div>
          <h1 style={{ fontSize: "20px", fontWeight: 700, color: theme.text, margin: 0 }}>
            Location Settings
          </h1>
          <p style={{ fontSize: "12px", color: theme.textSecondary, margin: "4px 0 0 0" }}>
            {restaurantName || "Restaurant"} - Update your location on map
          </p>
        </div>
      </div>

      {/* Search Section */}
      <div style={{ marginBottom: "16px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
        <div style={{
          flex: 1,
          minWidth: "250px",
          display: "flex",
          background: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: "10px",
          overflow: "hidden",
        }}>
          <input
            type="text"
            placeholder="Search location (e.g., Delhi, Mumbai, or coordinates)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && !isSearching && searchLocation()}
            style={{
              flex: 1,
              padding: "10px 14px",
              border: "none",
              outline: "none",
              background: theme.surface,
              color: theme.text,
              fontSize: "13px",
            }}
          />
          <button
            onClick={searchLocation}
            disabled={isSearching}
            style={{
              padding: "8px 16px",
              border: "none",
              background: isSearching ? "#64748b" : "#3b82f6",
              color: "#fff",
              cursor: isSearching ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            {isSearching ? <SpinnerIcon /> : <SearchIcon />}
            {isSearching ? "Searching..." : "Search"}
          </button>
        </div>

        <button
          onClick={getCurrentLocation}
          style={{
            padding: "8px 16px",
            borderRadius: "8px",
            border: `1px solid ${theme.border}`,
            background: isDark ? "#0f1520" : "#f8fafc",
            color: "#3b82f6",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "12px",
            fontWeight: 500,
          }}
        >
          <LocationIcon /> My Location
        </button>
      </div>

      {/* Message Display */}
      {saveMessage && (
        <div style={{
          marginBottom: "16px",
          padding: "12px 14px",
          borderRadius: "8px",
          background: saveMessage.type === "success" ? "rgba(16,185,129,0.1)" 
            : saveMessage.type === "error" ? "rgba(239,68,68,0.1)" 
            : "rgba(59,130,246,0.1)",
          border: `1px solid ${saveMessage.type === "success" ? "#10b981" 
            : saveMessage.type === "error" ? "#ef4444" 
            : "#3b82f6"}`,
          color: saveMessage.type === "success" ? "#10b981" 
            : saveMessage.type === "error" ? "#ef4444" 
            : "#3b82f6",
          fontSize: "12px",
          animation: "slideIn 0.3s ease-in-out",
        }}>
          {saveMessage.text}
        </div>
      )}

      {/* Leaflet Map */}
      <MapContainer
        center={[markerPosition.lat, markerPosition.lng]}
        zoom={13}
        style={{
          width: "100%",
          height: "450px",
          borderRadius: "12px",
          marginBottom: "16px",
          border: `1px solid ${theme.border}`,
          background: theme.surface,
        }}
        whenReady={() => setMapLoaded(true)}
        ref={mapRef}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapClickHandler onMapClick={handleMapClick} />
        <GeocoderControl onLocationFound={handleLocationFound} />
        <CurrentLocationControl onLocationFound={handleLocationFound} />
        {markerPosition && (
          <Marker position={[markerPosition.lat, markerPosition.lng]} icon={customIcon}>
            <Popup>📍 Selected Location</Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Location Details Form */}
      <div style={{
        background: theme.surface,
        borderRadius: "14px",
        border: `1px solid ${theme.border}`,
        padding: "16px",
      }}>
        <h3 style={{
          fontSize: "14px",
          fontWeight: 600,
          marginBottom: "14px",
          color: theme.text,
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}>
          <LocationIcon style={{ color: "#3b82f6" }} />
          Location Details
        </h3>

        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div>
            <label style={{ fontSize: "11px", color: "#64748b", display: "block", marginBottom: "5px", fontWeight: 500 }}>
              Full Address
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "8px",
                border: `1px solid ${theme.border}`,
                background: isDark ? "#0f1520" : "#f8fafc",
                color: theme.text,
                fontSize: "12px",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <div>
              <label style={{ fontSize: "11px", color: "#64748b", display: "block", marginBottom: "5px", fontWeight: 500 }}>
                Latitude
              </label>
              <input
                type="number"
                value={formData.latitude}
                onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                step="0.0001"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: `1px solid ${theme.border}`,
                  background: isDark ? "#0f1520" : "#f8fafc",
                  color: theme.text,
                  fontSize: "12px",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: "11px", color: "#64748b", display: "block", marginBottom: "5px", fontWeight: 500 }}>
                Longitude
              </label>
              <input
                type="number"
                value={formData.longitude}
                onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                step="0.0001"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: `1px solid ${theme.border}`,
                  background: isDark ? "#0f1520" : "#f8fafc",
                  color: theme.text,
                  fontSize: "12px",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          <button
            onClick={saveLocationToAPI}
            disabled={savingLocation}
            style={{
              marginTop: "8px",
              padding: "11px 20px",
              borderRadius: "8px",
              border: "none",
              background: savingLocation ? "#64748b" : "#10b981",
              color: "#fff",
              cursor: savingLocation ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            {savingLocation ? (
              <><SpinnerIcon /> Saving...</>
            ) : (
              <><SaveIcon /> Save Location</>
            )}
          </button>

          <p style={{ fontSize: "10px", color: "#64748b", margin: 0, marginTop: "4px" }}>
            💡 Tip: Click on the map, search for a place, or use "My Location" button.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LocationTabComponent;